import { useAuthStore } from './store.js'

// Derives the UI session (display name, role label, and which app CONTEXT the
// user is in) from the stored user + access-token claims. The JWT carries the
// authoritative role/tenant data (rbac-and-permissions.md §6); we decode the
// payload client-side for display/routing only — never for authorization, which
// the API enforces server-side.

interface JwtClaims {
  platform_role?: string
  tenant_memberships?: { tenant_id: string; role: string }[]
  account_manager_for?: string[]
  /** Vertical of the primary tenant — gates vertical-specific nav (Agenda/Catálogo). */
  tenant_vertical?: string
  /** True once this session completed an MFA challenge. */
  mfa_verified?: boolean
}

function decodeJwtClaims(token: string | null): JwtClaims {
  if (token === null) return {}
  try {
    const payload = token.split('.')[1]
    if (payload === undefined) return {}
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json) as JwtClaims
  } catch {
    return {}
  }
}

const ROLE_LABELS: Record<string, string> = {
  platform_owner: 'Dueño de plataforma',
  platform_admin: 'Administrador',
  platform_operator: 'Operador',
  account_manager: 'Gerente de cuenta',
  tenant_owner: 'Dueño',
  tenant_admin: 'Administrador',
  agent: 'Asesor',
  tenant_agent: 'Asesor',
  // Display only. tenant_viewer is no longer offered anywhere in the UI (it
  // can't be invited or assigned), but memberships created before that keep
  // working — and their chip read "Miembro", a word the product uses nowhere.
  tenant_viewer: 'Visor',
}

export type AppContext = 'tenant' | 'admin'

/** The app context implied by a raw access token (operators → admin). Used to
 *  route to the right landing page right after login, before useSession runs. */
export function contextFromToken(token: string | null): AppContext {
  const claims = decodeJwtClaims(token)
  const isOperator =
    claims.platform_role !== undefined || (claims.account_manager_for?.length ?? 0) > 0
  return isOperator ? 'admin' : 'tenant'
}

/** Landing page for a context: operators get the console overview, clients get
 *  their workspace home. Operators have no tenant membership, so sending them to
 *  the client `/home` 404s (GET /home is tenant-scoped). */
export function homePathForContext(context: AppContext): string {
  return context === 'admin' ? '/admin' : '/home'
}

// The tenant roles that may manage the workspace (config, team, the agentes).
// Mirrors the catalog: tenant.config.write / tenant.users.invite /
// tenant.bots.skills.write all belong to exactly owner + admin. Used only to
// hide admin-only UI — the API still enforces every write server-side.
const MANAGE_ROLES = new Set(['tenant_owner', 'tenant_admin'])

// The tenant roles that may edit a contact's CRM profile. Mirrors the
// catalog's contacts.write grant: owner + admin + agent, not viewer. The web
// session carries role hints only (no per-permission list from the JWT), so
// this set is kept in sync with catalog.ts by hand — same trade-off as
// MANAGE_ROLES above. UI hint only; the API still enforces every write.
const EDIT_CONTACT_ROLES = new Set(['tenant_owner', 'tenant_admin', 'tenant_agent'])

// The tenant role that may MINT an administrator (invite one, or promote a
// member to one). Mirrors the catalog's `tenant.users.grant_admin` grant, which
// belongs to tenant_owner alone — an admin who picks "Administrador" gets a 403
// from POST /team/invitations and PATCH /team/members/:id. Deliberately no
// operator escape hatch: an operator has no tenant membership, so the API would
// refuse them too. UI hint only; the API still enforces it.
const GRANT_ADMIN_ROLES = new Set(['tenant_owner'])

export interface Session {
  name: string
  email: string
  initials: string
  /** Human-readable role label for the user chip. */
  role: string
  /** Which shell the user sees: INHERTA operators get `admin`, clients get `tenant`. */
  context: AppContext
  /** Can manage the workspace (owner/admin, or any operator) — gates admin-only
   *  tenant surfaces (Configuración, Agentes). UI hint only; not authorization. */
  canManageWorkspace: boolean
  /** Can edit a contact's CRM profile (owner/admin/agent, or any operator) —
   *  mirrors the catalog's contacts.write grant. Merging two contacts is a
   *  separate, manage-only action — see canManageWorkspace. UI hint only. */
  canEditContacts: boolean
  /** Can name administrators (invite one, or promote a member) — the owner-only
   *  `tenant.users.grant_admin` rail. Decides which roles the Equipo tab's
   *  Selects offer, so the UI never proposes something the API would refuse.
   *  UI hint only; not authorization. */
  canGrantAdmin: boolean
  /** Raw platform role (operators only): 'platform_owner' | 'platform_admin' |
   *  'platform_operator' | 'platform_account_manager'. Used to decide which
   *  roles this person may GRANT when inviting — the API enforces the same rule,
   *  this only keeps the UI from offering something that would be refused. */
  platformRole?: string
  /** Vertical of the user's primary tenant (tenant context only). Gates
   *  vertical-specific surfaces (Agenda/Catálogo are healthcare/doctor-only).
   *  Undefined for operators or tokens minted before the claim existed. */
  vertical?: string
  /** This session completed a second factor. Manage surfaces are gated on it
   *  server-side for tenant_owner / tenant_admin (the MFA choke in assertCan),
   *  so the shell uses it to keep AMBIENT background queries off routes that
   *  would answer auth_mfa_enrollment_required — a polled badge must never be
   *  what redirects someone out of the page they are reading. Never used to
   *  decide what a person may do: the API decides that. */
  mfaVerified: boolean
  /** Tenant display + accent (tenant context only). */
  tenant?: { name: string; accent: string }
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase() || 'U'
}

/**
 * UI session derived from the auth store + decoded JWT claims: display fields,
 * shell context (admin vs tenant), and permission HINTS only — authorization
 * stays server-side. Returns null when logged out (route guards redirect).
 */
export function useSession(): Session | null {
  const user = useAuthStore((s) => s.user)
  const token = useAuthStore((s) => s.accessToken)
  if (user === null) return null

  const claims = decodeJwtClaims(token)
  const isOperator =
    claims.platform_role !== undefined || (claims.account_manager_for?.length ?? 0) > 0
  const rawRole = claims.platform_role ?? claims.tenant_memberships?.[0]?.role ?? ''

  const canManageWorkspace =
    isOperator || (claims.tenant_memberships ?? []).some((m) => MANAGE_ROLES.has(m.role))
  const canEditContacts =
    isOperator || (claims.tenant_memberships ?? []).some((m) => EDIT_CONTACT_ROLES.has(m.role))
  const canGrantAdmin = (claims.tenant_memberships ?? []).some((m) => GRANT_ADMIN_ROLES.has(m.role))

  return {
    name: user.name,
    email: user.email,
    initials: initialsOf(user.name),
    role: ROLE_LABELS[rawRole] ?? 'Miembro',
    context: isOperator ? 'admin' : 'tenant',
    canManageWorkspace,
    canEditContacts,
    canGrantAdmin,
    mfaVerified: claims.mfa_verified === true,
    ...(claims.platform_role !== undefined ? { platformRole: claims.platform_role } : {}),
    ...(claims.tenant_vertical !== undefined ? { vertical: claims.tenant_vertical } : {}),
    // The JWT carries no tenant display name or accent, so these are the value
    // the shell paints for the one frame before GET /account/me resolves. They
    // are a loading state, NOT the displayed identity: AppLayout overrides both
    // with the real tenant's name and colour. Leaving a client's console reading
    // 'Mi espacio' was the bug this replaced.
    ...(isOperator ? {} : { tenant: { name: 'Mi espacio', accent: '#0A68FF' } }),
  }
}
